import { z } from 'zod';

/**
 * Movement tuning. Designer units only (meters, seconds, ms) — gravity and jump velocity are
 * ALWAYS derived: g = 2h / t², v0 = 2h / t. Presets live in content/tuning/movement.json.
 */
export const MovementTuningSchema = z.object({
  maxRunSpeed: z.number().positive(),
  maxWalkSpeed: z.number().positive(),
  /**
   * Top speed while sprint is held, as a multiple of maxRunSpeed. `min(1)` because a value below
   * 1 would make the sprint key a BRAKE, which no amount of tuning intent justifies.
   */
  sprintSpeedMul: z.number().min(1),
  groundAccel: z.number().positive(),
  groundDecel: z.number().positive(),
  airAccel: z.number().nonnegative(),
  jumpHeight: z.number().positive(),
  jumpTimeToApex: z.number().positive(),
  gravityFallMul: z.number().min(1),
  apexHangVelThreshold: z.number().nonnegative(),
  apexHangGravityMul: z.number().positive().max(1),
  jumpCutVelocityMul: z.number().positive().max(1),
  jumpCutMaxTimeMs: z.number().nonnegative(),
  terminalVelocity: z.number().positive(),
  coyoteMs: z.number().nonnegative(),
  jumpBufferMs: z.number().nonnegative(),
  brakeAccel: z.number().positive(),
  brakeDotThreshold: z.number().min(-1).max(0),
  hoverHoldDelayMs: z.number().nonnegative(),
  hoverKickImpulse: z.number().nonnegative(),
  hoverDurationMs: z.number().nonnegative(),
  hoverGravity: z.number().positive(),
  hoverVyMin: z.number(),
  hoverVyMax: z.number(),
  hoverAirAccel: z.number().nonnegative(),
  hoverMaxSpeed: z.number().positive(),
  hoverWarnMs: z.number().nonnegative(),
  ledgeGuardEnabled: z.boolean(),
  ledgeGuardProbeDist: z.number().positive(),
  ledgeGuardSpeedCutoff: z.number().min(0).max(1),
  rollSpeed: z.number().nonnegative(),
  rollDurationMs: z.number().nonnegative(),
  rollCooldownMs: z.number().nonnegative(),
  rollLaunchVy: z.number().nonnegative(),
  capsuleRadius: z.number().positive(),
  capsuleHeight: z.number().positive(),
});

export type MovementTuning = z.infer<typeof MovementTuningSchema>;

export const TuningFileSchema = z.object({
  defaultPreset: z.string(),
  presets: z.record(z.string(), MovementTuningSchema),
});

export type TuningFile = z.infer<typeof TuningFileSchema>;

/** Values computed from designer units. Recompute whenever tuning changes. */
export interface DerivedTuning {
  gravityRise: number;
  gravityFall: number;
  jumpVelocity: number;
}

export function derive(t: MovementTuning): DerivedTuning {
  const gravityRise = (2 * t.jumpHeight) / (t.jumpTimeToApex * t.jumpTimeToApex);
  return {
    gravityRise,
    gravityFall: gravityRise * t.gravityFallMul,
    jumpVelocity: (2 * t.jumpHeight) / t.jumpTimeToApex,
  };
}

export function loadTuningFile(raw: unknown): TuningFile {
  return TuningFileSchema.parse(raw);
}

export function getPreset(file: TuningFile, name: string): MovementTuning {
  const preset = file.presets[name];
  if (!preset) throw new Error(`Unknown tuning preset "${name}"`);
  return preset;
}

/**
 * Surface physics profile (content/tuning/surfaces.json) — world vocabulary, orthogonal to
 * character presets. frictionMul scales the three ground accelerations together (grip);
 * maxSpeedMul caps ground target speed; cadenceMul slows the visual foot cycle (foot-slip).
 */
export const SurfaceProfileSchema = z.object({
  frictionMul: z.number().positive().max(2).default(1),
  maxSpeedMul: z.number().positive().max(2).default(1),
  cadenceMul: z.number().positive().max(2).default(1),
});

export type SurfaceProfile = z.infer<typeof SurfaceProfileSchema>;

export type SurfaceMap = Record<string, SurfaceProfile>;

export const NEUTRAL_SURFACE: SurfaceProfile = { frictionMul: 1, maxSpeedMul: 1, cadenceMul: 1 };

const SurfacesFileSchema = z.object({
  surfaces: z.record(z.string(), SurfaceProfileSchema),
});

export function loadSurfaces(raw: unknown): SurfaceMap {
  return SurfacesFileSchema.parse(raw).surfaces;
}

export function surfaceFor(map: SurfaceMap | undefined, tag: string): SurfaceProfile {
  return map?.[tag] ?? NEUTRAL_SURFACE;
}
